import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { Toast } from "vant";
import router from "@/router/index";

// ✅ 环境映射配置
const URL_MAP: Record<string, string> = {
  dev: "https://tbr-gateway-api-dev.gtech.asia",
  test: "https://tbr-gateway-api-test.gtech.asia",
  dstest: "https://tbr-gateway-api-test.gtechcloud.cn",
  prod: "https://tbr-gateway-api.cn.gtech.asia"
};

// ✅ 获取环境变量（window 或 .env）
type EnvName = keyof typeof URL_MAP;

export function resolveEnvByHost(host: string): EnvName {
  if (host.includes("-dev")) return "dev";
  if (host.includes("-test")) return "test";
  if (host.includes(".gtechcloud")) return "dstest";
  if (host.includes(".cn")) return "prod";
  return "dev";
}

const ENV = resolveEnvByHost(window.location.host);
const API_URL = URL_MAP[ENV] || URL_MAP.dev;

// 定义通用接口
interface ApiResponse<T = any> {
  code: number | string;
  message?: string;
  data: T;
}

// ✅ 创建 axios 实例
const service: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 15000
});

// ✅ 请求拦截器
service.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const url = config.url;
    const data = config.data;
    if (url.startsWith("/dict-i18n/dict-items") || data.dictNames) {
      config.url = url + data.dictNames
    }
    const AuthToken = localStorage.getItem("token");

    if (!config.headers) config.headers = {};
    config.headers["Auth-Token"] = AuthToken || "";
    config.headers["X-System-Code"] = "K1_OP";
    config.headers["X-App-Version"] = "0.0.1";

    return config;
  },
  error => Promise.reject(error)
);

// ✅ 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { status } = response;
    const url = response.config.url;
    const { data } = response as AxiosResponse<{
      code: number | string;
      data: any;
      message: string;
    }>;
    if (status === 200) {
      if (url.includes("/dict-i18n/dict-items")) {
        return response;
      }
      if (data.code !== "0") {
        Toast(data.message || "请求错误");
        return Promise.reject(response);
      }
      return response;
    }
    return Promise.reject(response);
  },
  error => {
    let message = "网络异常";
    const response = error.response;

    if (response) {
      const { status, data } = response;
      switch (status) {
        case 401:
          message = "登录已过期，请重新登录";
          router.push("/login");
          break;
        case 404:
          message = data.message || "接口不存在";
          break;
        case 500:
          message = data.message || "服务器错误";
          break;
        default:
          message = data.message || "请求错误";
      }
    } else {
      message = "连接超时或网络异常";
    }

    // 兼容自定义配置 options.errorMessage
    if (error.config && (error.config as any).options.errorMessage !== false) {
      Toast(message);
    }

    return Promise.reject(error);
  }
);

// ✅ 参数签名函数
function verifySign(params: Record<string, any> = {}) {
  return {
    ...params,
    timestamp: Date.now()
    // 可在此添加签名逻辑
  };
}

// ✅ 通用请求函数
interface RequestOptions {
  errorMessage?: boolean;
}

export interface DictApiResponse {
  items: Record<string, any>;
}

type DictUrl = `/k2-scan/dict-i18n/dict-items${string}`;

// ✅ 字典接口
export function httpServer(
  url: DictUrl,
  params?: Record<string, any>,
  options?: RequestOptions,
  method?: "get" | "post" | "put" | "delete"
): Promise<DictApiResponse>;

// ✅ 普通接口
export function httpServer<T = any>(
  url: string,
  params?: Record<string, any>,
  options?: RequestOptions,
  method?: "get" | "post" | "put" | "delete"
): Promise<ApiResponse<T>>;


export function httpServer(
  url: string,
  params: Record<string, any> = {},
  options: RequestOptions = { errorMessage: true },
  method: "get" | "post" | "put" | "delete" = "post"
) {
  const config: AxiosRequestConfig = {
    url,
    method,
    [method === "get" ? "params" : "data"]: verifySign(params),
    ...(options ? { options } : {})
  };

  return service(config).then(res => res.data);
}


export { API_URL };
